"use client"

import * as React from "react"
import type { DateRange } from "react-day-picker"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { ChevronDownIcon } from "lucide-react"
import { useIntl } from "react-intl"
import { useSalonDateFnsLocale } from "@/hooks/me/use-salon"

interface DateRangePickerProps {
  id?: string
  from?: Date | null
  to?: Date | null
  onRangeChange?: (range: { from: Date; to: Date }) => void
  placeholder?: string
  disabled?: boolean
}

export function DateRangePicker({
  id,
  from,
  to,
  onRangeChange,
  placeholder = "Select dates",
  disabled = false,
}: DateRangePickerProps) {
  const [open, setOpen] = React.useState(false)
  const intl = useIntl()
  const locale = useSalonDateFnsLocale()
  const selected: DateRange | undefined =
    from != null
      ? { from, to: to ?? from }
      : undefined

  const label = from
    ? intl.formatDateTimeRange(from, to ?? from, { format: "short" })
    : placeholder

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          id={id}
          disabled={disabled}
          className="w-full justify-between font-normal sm:w-64"
        >
          {label}
          <ChevronDownIcon />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto overflow-hidden p-0" align="start">
        <Calendar
          mode="range"
          locale={locale}
          selected={selected}
          captionLayout="dropdown"
          defaultMonth={from || undefined}
          onSelect={(range) => {
            if (!range?.from) return
            const nextTo = range.to ?? range.from
            onRangeChange?.({ from: range.from, to: nextTo })
            if (range.to) setOpen(false)
          }}
        />
      </PopoverContent>
    </Popover>
  )
}
